import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
import CustomCursor from '../components/CustomCursor';
import SignalNavigation from '../components/SignalNavigation';
import WhatsAppButton from '../components/WhatsAppButton';
import BoldHeroSection from '../components/sections/BoldHeroSection';
import BoldAboutSection from '../components/sections/BoldAboutSection';
import BoldExperienceSection from '../components/sections/BoldExperienceSection';
import BoldServicesSection from '../components/sections/BoldServicesSection';
import BoldProjectsSection from '../components/sections/BoldProjectsSection';
import BoldSocialMediaSection from '../components/sections/BoldSocialMediaSection';
import BoldSkillsSection from '../components/sections/BoldSkillsSection';
import BoldTestimonialsSection from '../components/sections/BoldTestimonialsSection';
import BoldContactSection from '../components/sections/BoldContactSection';
import Footer from '../components/sections/Footer';

const BoldPortfolio = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <html lang="en" />
        <title>Maheen Kehkasha | Digital Marketer & Social Media Manager in Bengaluru</title>
        <meta 
          name="description" 
          content="Digital marketer in Bengaluru blending SEO, paid social, and content into systems that convert attention into revenue." 
        />
        <meta 
          name="keywords" 
          content="digital marketer Bengaluru, social media manager, SEO specialist, Meta ads expert, MERN developer, freelance digital marketing" 
        />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Maheen Kehkasha | Digital Marketer & Social Media Manager in Bengaluru" />
        <meta property="og:description" content="Digital marketer in Bengaluru blending SEO, paid social, and content into systems that convert attention into revenue." />
        <meta property="og:url" content="https://maheenkehkasha.netlify.app" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Maheen Kehkasha | Digital Marketer & Social Media Manager" />
        <meta name="twitter:description" content="Digital marketer in Bengaluru blending SEO, paid social, and content into systems that convert attention into revenue." />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Maheen Kehkasha",
            "jobTitle": "Digital Marketer & Social Media Manager",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Bengaluru",
              "addressCountry": "India"
            },
            "email": "connectmaheenk@gmail.com",
            "telephone": "+917899275871",
            "url": "https://maheenkehkasha.netlify.app"
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Maheen Kehkasha Digital Marketing",
            "serviceType": [
              "SEO Services", 
              "Social Media Management", 
              "Meta Advertising", 
              "Website Development", 
              "Digital Marketing Consulting"
            ],
            "provider": {
              "@type": "Person",
              "name": "Maheen Kehkasha"
            },
            "areaServed": "Bengaluru, India"
          })}
        </script>
      </Helmet>

      {/* Custom Cursor (Desktop Only) */}
      <CustomCursor />

      {/* Navigation */}
      <SignalNavigation onContactClick={() => scrollToSection('contact')} />

      {/* WhatsApp Button */}
      <WhatsAppButton />

      {/* Scroll to Top */}
      {showScrollTop && (
        <motion.button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-6 z-40 w-14 h-14 bg-gradient-to-r from-[#7C3AED] to-[#C026D3] rounded-full flex items-center justify-center shadow-2xl hover:shadow-[#7C3AED]/50 transition-shadow"
          aria-label="Scroll to top"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1, rotate: 180 }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronUp size={28} className="text-white" />
        </motion.button>
      )}

      <div className="min-h-screen bg-[#0E0B1F]">
        <BoldHeroSection scrollToSection={scrollToSection} />
        <BoldAboutSection />
        <BoldExperienceSection />
        <BoldServicesSection />
        <BoldProjectsSection />
        <BoldSocialMediaSection />
        <BoldSkillsSection />
        <BoldTestimonialsSection />
        <BoldContactSection />
        <Footer />
      </div>
    </>
  );
};

export default BoldPortfolio;
